import { useMemo } from 'react';
import { Params, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useGetCustomerById } from '@utils/api/back-office/customer-bo/use-get-customer-bo';
import { useGetCustomerExceedances } from '@utils/api/back-office/exceedances/use-get-customer-exceedances';
import Breadcrumb from '@components/atoms/ui-kit/breadcrumb';
import PageHeader from '@components/layout/page-header';
import DataGridV2, {
  type DataGridColumn
} from '@components/atoms/datatable/data-grid-v2';
import Loader from '@components/atoms/loaders/loader';
import {
  Exceedance,
  OrgExceedance
} from '@utils/types/back-office/exceedances';
import { PATHS } from '../../../router/paths';
import { useThemeContext } from '@utils/hooks/use-theme-context';
import { fmtNumber } from '@utils/format';
import { StatusBadge } from '@components/atoms/ui-kit/badge';

type ExceedanceRow = Exceedance &
  Pick<OrgExceedance, 'organization_id' | 'organization_name'> & { id: string };

export default function CustomerBoExceedancesPage() {
  const { customerId } = useParams<Params>();
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const { data: customerData, isError } = useGetCustomerById(
    Number(customerId)
  );
  const { data, isLoading } = useGetCustomerExceedances(customerId || '');

  const customerName = customerData?.result?.name || t('customer');

  const breadcrumbItems = [
    { label: t('customers'), path: PATHS().CUSTOMERS },
    { label: customerName, path: PATHS(customerId || '').CUSTOMER_CONTENT },
    { label: t('exceedances') }
  ];

  const columns = useMemo<DataGridColumn<ExceedanceRow>[]>(
    () => [
      {
        id: 'organization_name',
        accessorKey: 'organization_name',
        header: t('organization'),
        minWidth: 180,
        enableSorting: false,
        enableFiltering: false
      },
      {
        id: 'type',
        accessorKey: 'type',
        header: t('type'),
        enableSorting: false,
        enableFiltering: false,
        maxWidth: 140,
        cell: info => {
          const type = info.getValue() as string;
          return <StatusBadge status={type} label={t(type)} />;
        }
      },
      {
        id: 'limit',
        accessorKey: 'limit',
        header: t('limit'),
        maxWidth: 120,
        enableSorting: false,
        enableFiltering: false,
        cell: info => {
          const value = Number(info.getValue()) || 0;
          return (
            <span style={{ color: getColour('normalText') }}>
              {fmtNumber(value)}
            </span>
          );
        }
      },
      {
        id: 'used',
        accessorKey: 'used',
        header: t('used'),
        maxWidth: 120,
        enableSorting: false,
        enableFiltering: false,
        cell: info => {
          const value = Number(info.getValue()) || 0;
          return (
            <span style={{ color: getColour('normalText') }}>
              {fmtNumber(value)}
            </span>
          );
        }
      },
      {
        id: 'exceeded_by',
        accessorKey: 'exceeded_by',
        header: t('exceeded-by'),
        maxWidth: 130,
        enableSorting: false,
        enableFiltering: false,
        cell: info => {
          const value = Number(info.getValue()) || 0;

          return (
            <span className="font-semibold text-red-700 dark:text-red-400">
              {fmtNumber(value)}
            </span>
          );
        }
      },
      {
        id: 'plan_sku',
        accessorKey: 'plan_sku',
        header: t('plan'),
        maxWidth: 160,
        enableSorting: false,
        enableFiltering: false
      },
      {
        id: 'period_start',
        accessorKey: 'period_start',
        header: t('period'),
        maxWidth: 180,
        enableSorting: false,
        enableFiltering: false,
        cell: info => {
          const row = info.row.original;
          return (
            <span style={{ color: getColour('normalText') }}>
              {row.period_start} → {row.period_end}
            </span>
          );
        }
      }
    ],
    [t, getColour]
  );

  const rows: ExceedanceRow[] = useMemo(
    () =>
      (data?.result || []).flatMap(item =>
        item.exceedances.map((exc, idx) => ({
          ...exc,
          organization_id: item.organization_id,
          organization_name: item.organization_name,
          id: `${item.organization_id}-${exc.type}-${idx}`
        }))
      ),
    [data]
  );

  if (isError) {
    return (
      <div className="bg-app flex-1 flex items-center justify-center">
        <p className="text-sm">{t('error-loading-customer')}</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="bg-app flex-1 w-full flex justify-center items-center">
        <Loader height={10} width={3} />
      </div>
    );
  }

  return (
    <div className="bg-app flex-1">
      <div className="px-4 py-2">
        <Breadcrumb items={breadcrumbItems} />
        <PageHeader
          title={`${customerName} — ${t('exceedances')}`}
          caption={t('plan-limit-exceedances-for-customer')}
        />
        <div className="mt-4">
          <DataGridV2<ExceedanceRow>
            columns={columns}
            data={rows}
            enablePagination
            enableSorting
            enableRowSelection={false}
          />
        </div>
      </div>
    </div>
  );
}
